import React, { useEffect, useState, useCallback } from 'react'
import styled from '@emotion/styled'

import {
  FlexContainer,
  VendorPricingAgreementTiersDisplay,
  Panel,
} from '@components/common'

import { VendorPricingAgreementsReports } from '@containers/vendor-pricing-agreements'
import {
  VendorPricingAgreement,
  VendorPricingAgreementScopes,
  VendorPricingAgreementServiceTypes,
  VendorPricingAgreementDiscountTypes,
} from '@types'

import { colors } from '@constants'

interface VendorPricingAgreementsProps {
  vendorPricingAgreements: VendorPricingAgreement[] | null
  getUniversalVendorPricingAgreements: () => Promise<
    VendorPricingAgreement[] | null
  >
  editVendorPricingAgreement: (
    vendorPricingAgreement: VendorPricingAgreement,
  ) => void
}

const VendorPricingAgreements: React.FC<VendorPricingAgreementsProps> = ({
  vendorPricingAgreements,
  getUniversalVendorPricingAgreements,
  editVendorPricingAgreement,
}) => {
  const [tab, setTab] = useState<'edit' | 'reports'>('edit')

  const refreshAgreements = useCallback(async () => {
    await getUniversalVendorPricingAgreements()
  }, [getUniversalVendorPricingAgreements])

  useEffect(() => {
    refreshAgreements()
  }, [refreshAgreements])

  return (
    <FlexContainer
      width="100%"
      alignItems="center"
      flexDirection="column"
      margin="20px 0"
    >
      <FlexContainer
        width="100%"
        alignItems="center"
        justifyContent="center"
        flexDirection="row"
        margin="10px 0"
      >
        <VendorPricingAgreementTabButton
          isActive={tab === 'edit'}
          onClick={() => setTab('edit')}
        >
          Edit
        </VendorPricingAgreementTabButton>
        <VendorPricingAgreementTabButton
          isActive={tab === 'reports'}
          onClick={() => setTab('reports')}
        >
          Reports
        </VendorPricingAgreementTabButton>
      </FlexContainer>
      <FlexContainer
        width="100%"
        alignItems="center"
        justifyContent="center"
        flexDirection="column"
        padding="20px 20px"
      >
        {tab === 'edit' && (
          <VendorPricingAgreementsTab
            vendorPricingAgreements={vendorPricingAgreements}
            editVendorPricingAgreement={editVendorPricingAgreement}
          />
        )}
        {tab === 'reports' && <VendorPricingAgreementReportsTab />}
      </FlexContainer>
    </FlexContainer>
  )
}

export default VendorPricingAgreements

interface VendorPricingAgreementsTabProps {
  vendorPricingAgreements: VendorPricingAgreement[] | null
  editVendorPricingAgreement: (
    vendorPricingAgreement: VendorPricingAgreement,
  ) => void
}

const VendorPricingAgreementsTab: React.FC<VendorPricingAgreementsTabProps> = ({
  vendorPricingAgreements,
  editVendorPricingAgreement,
}) => {
  const [pricingAgreements, setVendorPricingAgreements] = useState<
    VendorPricingAgreement[] | null
  >(vendorPricingAgreements)

  useEffect(() => {
    setVendorPricingAgreements(vendorPricingAgreements)
  }, [vendorPricingAgreements])

  const onCreateVendorPricingAgreement = () => {
    editVendorPricingAgreement({
      id: '',
      scope: VendorPricingAgreementScopes.UNIVERSAL,
      serviceTypes: [
        VendorPricingAgreementServiceTypes.CATERING,
        VendorPricingAgreementServiceTypes.GROUP_ORDER,
      ],
      vendorId: null,
      hqId: null,
      discountType: VendorPricingAgreementDiscountTypes.PERCENTAGE,
      tiers: [
        {
          position: 1,
          lowerLimit: 0,
          upperLimit: null,
          discountPercentage: 1,
        },
      ],
      active: true,
    })
  }

  if (!pricingAgreements) {
    return (
      <FlexContainer
        width="100%"
        alignItems="center"
        justifyContent="center"
        flexDirection="column"
      >
        <p className="mb-5 text-md mt-10">
          No universal vendor pricing agreement exists.
        </p>
        <button
          className="px-3 py-3 bg-blue-500 text-white rounded-md"
          onClick={onCreateVendorPricingAgreement}
        >
          Create Universal Vendor Pricing Agreement
        </button>
      </FlexContainer>
    )
  }

  return (
    <FlexContainer
      width="100%"
      alignItems="center"
      justifyContent="center"
      flexDirection="column"
    >
      <VendorPricingAgreementTiersDisplay
        pricingAgreements={pricingAgreements}
        title="Universal Vendor Pricing Agreements"
        width="66%"
        onCreateVendorPricingAgreement={onCreateVendorPricingAgreement}
        editVendorPricingAgreement={editVendorPricingAgreement}
      />
    </FlexContainer>
  )
}

const VendorPricingAgreementReportsTab: React.FC = () => {
  return (
    <Panel heading="Vendor Pricing Agreements Reports" width="50%">
      <VendorPricingAgreementsReports />
    </Panel>
  )
}

const VendorPricingAgreementTabButton = styled.button<{ isActive: boolean }>`
  font-family: 'bold';
  margin-right: 20px;
  margin-left: 20px;
  font-size: 28px;
  color: ${colors.gray400};
  border: none;
  border-bottom: 5px solid transparent;
  padding-bottom: 3px;
  cursor: pointer;
  transition: all 0.2s ease;
  ${({ isActive }) =>
    isActive &&
    `
    border-bottom: 2px solid ${colors.orange};
  `}
`
